import * as React from 'react';
import { IPlayWidgetExtended, IPlayOutlookCalendarEvent } from '../../../../interfaces/IPlay';
import * as styles from './OutlookCalender.css';
import Moment from 'react-moment';
import { LocalizationService } from '../../../../services/LocalizationService';

export interface IProps {
  widget: IPlayWidgetExtended;
  headlineFont: string;
  sizeUnit: number;
  height: number;
  width: number;
  willRestart: () => void;
}

export interface IState {
  pageNumber: number;
  eventsPerPage: number;
  timerProgress: number;
}

export class OutlookCalender extends React.Component<IProps, IState> {
  private pageTimer: NodeJS.Timeout | null = null;
  private animationTimer: NodeJS.Timeout | null = null;

  constructor(props: IProps) {
    super(props);
    this.state = {
      eventsPerPage: undefined,
      pageNumber: 1,
      timerProgress: 0,
    };
  }

  public componentWillReceiveProps(props: IProps): void {
    if (props.height && props.sizeUnit && !this.state.eventsPerPage) {
      // Headline and date share the same row; height is determined by the larger of the two
      const headerHeight: number = this.props.widget?.outlookCalendar?.headline
        ? props.sizeUnit * 4.6
        : (this.props.widget?.outlookCalendar?.showDate ? props.sizeUnit * 2 : 0);
      const availableHeightForEvents: number = props.height - headerHeight - (props.sizeUnit * 10);
      // Each event takes sizeUnit * 7 (height) + sizeUnit * 2 (marginBottom) = sizeUnit * 9
      const eventsPerPage: number = Math.floor(availableHeightForEvents / (props.sizeUnit * 9));
      this.setState({ eventsPerPage }, () => {
        this.forceUpdate();
        this.startPageTimer();
      });
    }
  }

  public componentDidMount(): void {
    if (this.state.eventsPerPage) {
      this.startPageTimer();
    }
  }

  public componentWillUnmount(): void {
    this.clearPageTimer();
    this.clearAnimationTimer();
  }

  private startPageTimer = (): void => {
    this.clearPageTimer();
    this.clearAnimationTimer();
    if (this.props.widget?.outlookCalendar?.events && this.state.eventsPerPage) {
      const totalPages = Math.ceil(this.props.widget.outlookCalendar?.events?.length / this.state.eventsPerPage);
      // Only cycle through pages when there's more than one page to show.
      // willRestart() must still fire on a single page (or zero events), so
      // it's scheduled unconditionally below - otherwise, with showAll set
      // and totalPages <= 1, no timer would ever be armed (the Widget-level
      // fallback restart is also disabled whenever showAll is true) and the
      // slide would get stuck forever.
      const shouldCyclePages = totalPages > 1 && this.props.widget?.outlookCalendar?.showAll;

      this.setState({ timerProgress: 0 });
      if (shouldCyclePages) {
        this.startTimerAnimation();
      }

      this.pageTimer = setTimeout(() => {
        if (!shouldCyclePages || this.state.pageNumber === totalPages) {
          this.props.willRestart();
        }
        if (shouldCyclePages) {
          this.setState(prevState => ({
            pageNumber: prevState.pageNumber >= totalPages ? 1 : prevState.pageNumber + 1
          }), () => {
            this.startPageTimer();
          });
        }
      }, this.props.widget?.duration * 1000 || 10000);
    }
  }

  private startTimerAnimation = (): void => {
    const totalDuration = this.props.widget?.duration * 1000 || 10000;
    const updateInterval = 50; // Update every 50ms for smooth animation
    const increment = (updateInterval / totalDuration) * 100;

    this.animationTimer = setInterval(() => {
      this.setState(prevState => {
        const newProgress = prevState.timerProgress + increment;
        if (newProgress >= 100) {
          this.clearAnimationTimer();
          return { timerProgress: 100 };
        }
        return { timerProgress: newProgress };
      });
    }, updateInterval);
  }

  private clearPageTimer = (): void => {
    if (this.pageTimer) {
      clearTimeout(this.pageTimer);
      this.pageTimer = null;
    }
  }

  private clearAnimationTimer = (): void => {
    if (this.animationTimer) {
      clearInterval(this.animationTimer);
      this.animationTimer = null;
    }
  }

  private renderCountdownCircle = (): JSX.Element | null => {
    if (!this.state.eventsPerPage || !this.props.widget?.outlookCalendar?.events || !this.props.widget?.outlookCalendar?.showAll) {
      return null;
    }

    const totalPages = Math.ceil(this.props.widget?.outlookCalendar?.events?.length / this.state.eventsPerPage);
    if (totalPages <= 1) {
      return null;
    }

    const size = this.props.sizeUnit * 3;
    const strokeWidth = this.props.sizeUnit * 0.3;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (this.state.timerProgress / 100) * circumference;

    return (
      <svg
        width={size}
        height={size}
        style={{
          marginTop: this.props.sizeUnit
        }}
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={this.props.widget?.outlookCalendar?.headlineColor || this.props.widget?.outlookCalendar?.highlightColor || '#333333'}
          strokeWidth={strokeWidth}
          opacity={0.3}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={this.props.widget?.outlookCalendar?.headlineColor || this.props.widget?.outlookCalendar?.highlightColor || '#333333'}
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{
            transition: 'stroke-dashoffset 0.05s ease-out'
          }}
        />
      </svg>
    );
  }

  private getColumnWidth(): string {
    let columns = 0;
    const timeWidth = this.props.widget?.outlookCalendar?.showTime ? this.props.sizeUnit * 25 : 0;
    if (this.props.widget?.outlookCalendar?.showMeetingTitle) {
      columns++
    }
    if (this.props.widget?.outlookCalendar?.showOrganizer) {
      columns++
    }
    if (this.props.widget?.outlookCalendar?.showRoom) {
      columns++
    }
    return `calc(${100 / columns}% - ${(timeWidth / columns)}px)`;
  }

  private getEventDisplayName(event: IPlayOutlookCalendarEvent): string {
    const room = event?.locations?.find(l => l.locationType === 'conferenceRoom');

    // If a conferenceRoom exists, use its display name; otherwise, fall back to the organizer's name
    return room?.displayName ?? event?.organizer?.emailAddress?.name ?? "";
  }

  private getEventOrganizerName(event: IPlayOutlookCalendarEvent): string {
    const hasRoom = event?.locations?.some(l => l.locationType === 'conferenceRoom');

    // If no conferenceRoom is found, organizerName is set to "Occupied"
    if (!hasRoom) {
      return new LocalizationService().strings.Player_EventOccupied;
    }

    return event?.organizer?.emailAddress?.name ?? "";
  }

  public render(): JSX.Element {
    const pageCount = this.props.widget?.outlookCalendar?.events && this.state.eventsPerPage ? Math.ceil(this.props.widget.outlookCalendar.events.length / this.state.eventsPerPage) : 1;
    return (
      <div
        className={styles.IA_outlookCalendarContainer}
        style={{
          padding: this.props.sizeUnit * 5,
          gap: this.props.sizeUnit,
          backgroundColor: this.props.widget?.outlookCalendar?.backgroundColor || '#000000',
        }}
      >
        {(this.props.widget?.outlookCalendar?.headline || this.props.widget?.outlookCalendar?.showDate) &&
          <div
            className={styles.IA_headline}
            style={{
              fontFamily: this.props.headlineFont,
              color: this.props.widget?.outlookCalendar?.headlineColor || '#333333',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              gap: `${this.props.sizeUnit * 0.75}px ${this.props.sizeUnit}px`,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flex: '0 0 auto',
                maxWidth: '100%',
                minWidth: 0,
                overflow: 'hidden',
                gap: this.props.sizeUnit,
                width: this.props.height > this.props.width ? '100%' : 'auto',
              }}
            >
              {this.props.widget?.outlookCalendar?.headline &&
                <span
                  style={{
                    fontSize: `${this.props.sizeUnit * 4.6}px`,
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {pageCount > 1 && this.props.widget?.outlookCalendar?.showAll ? `${this.props.widget?.outlookCalendar?.headline} (${this.state.pageNumber}/${pageCount})` : this.props.widget?.outlookCalendar?.headline}
                </span>
              }
              {this.renderCountdownCircle()}
            </div>
            {this.props.widget?.outlookCalendar?.showDate &&
              <Moment
                style={{
                  fontSize: `${this.props.sizeUnit * 2}px`,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
                format="dddd, LL"
                locale={navigator.language}
              >
                {new Date()}
              </Moment>
            }
          </div>
        }
        <div
          className={styles.IA_outlookCalendarEventsContainer}
          style={{
            marginTop: this.props.sizeUnit * 2,
          }}
        >
          {this.props.widget?.outlookCalendar?.events?.length === 0 &&
            <div
              style={{
                fontSize: `${this.props.sizeUnit * 3}px`,
                fontFamily: this.props.headlineFont,
                color: this.props.widget?.outlookCalendar?.headlineColor || '#333333',
                opacity: 0.5,
              }}
            >
              {this.props.widget?.outlookCalendar?.noEventsLabel ? this.props.widget?.outlookCalendar?.noEventsLabel : new LocalizationService().strings.PlayAdmin_OutlookCalendarNoEventsLabel}
            </div>
          }
          {this.props.widget?.outlookCalendar?.events?.sort((a, b) => {
            const startComparison = new Date(a.start.dateTime).getTime() - new Date(b.start.dateTime).getTime();
            if (startComparison !== 0) {
              return startComparison;
            }
            return new Date(a.end.dateTime).getTime() - new Date(b.end.dateTime).getTime();
          })?.slice((this.state.pageNumber - 1) * (this.state.eventsPerPage || 0), this.state.pageNumber * (this.state.eventsPerPage || 0))
            .map((event, index) => (
              <div key={index}
                className={styles.IA_outlookCalendarEvent}
                style={{
                  fontSize: `${this.props.sizeUnit * 3}px`,
                  lineHeight: `${this.props.sizeUnit * 7}px`,
                  height: `${this.props.sizeUnit * 7}px`,
                  fontFamily: this.props.headlineFont,
                  color: this.props.widget?.outlookCalendar?.textColor || '#ffffff',
                  backgroundColor: this.props.widget?.outlookCalendar?.highlightColor || '#fc8a00',
                  borderRadius: this.props.sizeUnit * 2,
                  marginBottom: this.props.sizeUnit * 2,
                  padding: `0 ${this.props.sizeUnit * 2}px`,
                  boxSizing: 'border-box',
                }}
              >
                <div>
                  {this.props.widget?.outlookCalendar?.showTime && (
                    <div
                      className={styles.IA_outlookCalendarEventItem}
                      style={{
                        padding: `0 ${this.props.sizeUnit * 1}px`,
                        width: this.props.sizeUnit * 25,
                      }}
                    >
                      <Moment
                        format="HH:mm"
                        locale={navigator.language}
                        utc
                        local
                      >
                        {event?.start?.dateTime}
                      </Moment>
                      {` - `}
                      <Moment
                        format="HH:mm"
                        locale={navigator.language}
                        utc
                        local
                      >
                        {event?.end?.dateTime}
                      </Moment>
                    </div>
                  )}
                  {this.props.widget?.outlookCalendar?.showMeetingTitle && (
                    <div
                      className={styles.IA_outlookCalendarEventItem}
                      style={{
                        padding: `0 ${this.props.sizeUnit * 1}px`,
                        width: this.getColumnWidth()
                      }}
                    >
                      {event?.subject}
                    </div>
                  )}
                  {this.props.widget?.outlookCalendar?.showOrganizer && (
                    <div
                      className={styles.IA_outlookCalendarEventItem}
                      style={{
                        padding: `0 ${this.props.sizeUnit * 1}px`,
                        width: this.getColumnWidth()
                      }}
                    >

                      {this.getEventOrganizerName(event)}

                    </div>
                  )}
                </div>
                <div>
                  {this.props.widget?.outlookCalendar?.showRoom && (
                    <div
                      className={styles.IA_outlookCalendarEventItem}
                      style={{
                        padding: `0 ${this.props.sizeUnit * 1}px`,
                        width: this.getColumnWidth(),
                        float: 'right',
                        textAlign: 'right',
                      }}
                    >

                      {this.getEventDisplayName(event)}

                    </div>
                  )}
                </div>
              </div>
            ))
          }
        </div>
      </div>
    );
  }
}